import { z } from 'zod';
import { beteiligterSchema, type Beteiligter } from './protokoll-daten.js';

/**
 * Persönliches Profil eines Nutzers (SPEC Kap. 12).
 *
 * Zweck ist ausschließlich Vorbelegung: Wer vor Ort ein Protokoll anlegt, soll
 * seinen eigenen Namen, seine Funktion und seine Erreichbarkeit nicht bei jeder
 * Übergabe erneut eintippen. Fachlich verbindlich ist immer das, was im
 * Protokoll steht — das Profil füllt nur die Felder vor und lässt sich dort
 * jederzeit überschreiben.
 *
 * Bewusst **nicht** im Profil: die Unterschrift. Eine einfache elektronische
 * Signatur entsteht vor Ort im Beisein der Beteiligten (§ 127 BGB); eine
 * gespeicherte und wiederverwendete Unterschrift wäre genau das nicht.
 */

/** Rolle, unter der die meisten Erfasser im Protokoll auftreten. */
export const PROFIL_ROLLE_STANDARD = 'Vermieter';

/** Vorschläge für die eigene Rolle — die Vorlage kann andere Rollen führen. */
export const PROFIL_ROLLEN_VORSCHLAEGE = [
  'Vermieter',
  'Verwalter',
  'Eigentümer',
  'Hausmeister',
  'Makler',
  'Bauleitung',
] as const;

export const profilSchema = z.object({
  /** Name, wie er im Protokoll und unter der Unterschrift erscheinen soll. */
  anzeigename: z.string().max(200).default(''),
  funktion: z.string().max(120).default(''),
  firma: z.string().max(200).default(''),
  telefon: z.string().max(60).default(''),
  email: z.email('Keine gültige E-Mail-Adresse').or(z.literal('')).default(''),
  /** Rolle, in der der Nutzer üblicherweise unterschreibt. */
  standardRolle: z.string().max(60).default(PROFIL_ROLLE_STANDARD),
  /** Trägt sich bei einem neuen Protokoll automatisch als Beteiligter ein. */
  alsBeteiligterEintragen: z.boolean().default(true),
  /** Merkt den Nutzer als Empfänger des fertigen Protokolls vor. */
  erhaeltProtokoll: z.boolean().default(false),
});

export type Profil = z.infer<typeof profilSchema>;

export const PROFIL_STANDARD: Profil = profilSchema.parse({});

/** Liest ein Profil tolerant — ein unbrauchbares Feld darf die App nicht lahmlegen. */
export const profilLesen = (wert: unknown): Profil => {
  const geprueft = profilSchema.safeParse(wert ?? {});
  return geprueft.success ? geprueft.data : PROFIL_STANDARD;
};

/**
 * Name für Anzeige und `erfasstVon`.
 *
 * Ohne Profil bleibt der Anmeldename übrig. Der ist eine E-Mail-Adresse und
 * damit als Klarname im Protokoll unbrauchbar — aber besser als eine Leerzeile.
 */
export const profilAnzeigename = (
  profil: Pick<Profil, 'anzeigename'> | null | undefined,
  ersatz = '',
): string => {
  const name = profil?.anzeigename?.trim() ?? '';
  return name !== '' ? name : ersatz.trim();
};

/** `true`, sobald im Profil überhaupt etwas Verwertbares steht. */
export const profilGepflegt = (profil: Profil | null | undefined): boolean =>
  profil !== null && profil !== undefined && profil.anzeigename.trim() !== '';

/**
 * Der Nutzer selbst als Beteiligter — oder `null`, wenn das Profil dafür nicht
 * reicht. Ohne Namen wäre der Eintrag im Protokoll wertlos.
 */
export const beteiligterAusProfil = (profil: Profil | null | undefined): Beteiligter | null => {
  if (!profil || !profil.alsBeteiligterEintragen) return null;

  const name = profil.anzeigename.trim();
  if (name === '') return null;

  const firma = [profil.firma.trim(), profil.funktion.trim()].filter(Boolean).join(' · ');

  return beteiligterSchema.parse({
    rolle: profil.standardRolle.trim() || PROFIL_ROLLE_STANDARD,
    name,
    email: profil.email.trim(),
    telefon: profil.telefon.trim(),
    firma,
    erhaeltProtokoll: profil.erhaeltProtokoll,
  });
};

/**
 * Ergänzt die Beteiligtenliste um den Nutzer selbst.
 *
 * Steht er schon drin — gleiche Rolle **oder** gleicher Name —, bleibt der
 * vorhandene Eintrag unangetastet. Sonst würde eine Korrektur vor Ort beim
 * nächsten Öffnen wieder von der Vorbelegung überschrieben.
 */
export const beteiligteMitProfil = (
  beteiligte: Beteiligter[],
  eigener: Beteiligter | null,
): Beteiligter[] => {
  if (!eigener) return beteiligte;

  const vergleich = (wert: string): string => wert.trim().toLowerCase();
  const vorhanden = beteiligte.some(
    (person) =>
      vergleich(person.name) === vergleich(eigener.name) ||
      vergleich(person.rolle) === vergleich(eigener.rolle),
  );

  return vorhanden ? beteiligte : [...beteiligte, eigener];
};
